import React, { useMemo } from 'react';

import type { ColumnProps } from 'antd/lib/table/interface';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { pendingItemsNotApprovableSel } from '@modules/advancement/pendingItems/duck/selectorsTyped';
import { NonApprovablePendingItem } from '@modules/advancement/pendingItems/types';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { DateView, PersonAvatar, S, Table } from '@shared/components';

import PendingItemAdvancementName from '../PendingItemAdvancementName';
import PendingItemType from '../PendingItemType';
import styles from './NotApprovableItems.less';

export function NotApprovableItemsTable(props: { isLoading?: boolean }) {
  const notApprovableItems = useSelector(pendingItemsNotApprovableSel);

  const cols: ColumnProps<NonApprovablePendingItem>[] = useMemo(
    () => [
      {
        key: 'avatar',
        width: '5.5rem',
        align: 'right',
        render(_, record) {
          return (
            <PersonAvatar
              isAdult={record.isAdult}
              src={record.profilePictureUrl || ''}
            />
          );
        },
      },
      {
        key: 'name',
        width: '20rem',
        title: <FormattedMessage id="advancement.PendingItems.column.name" />,
        render(_, { firstName, lastName, nickName }) {
          const personPreferredName = getPreferredName({
            firstName,
            lastName,
            nickName,
          });
          return (
            <S size="4" bold inline colored title={personPreferredName}>
              {personPreferredName}
            </S>
          );
        },
      },
      {
        dataIndex: 'memberId',
        title: (
          <FormattedMessage id={'advancement.PendingItems.column.memberId'} />
        ),
        width: '15ch',
      },
      {
        key: 'advancementType',
        title: (
          <FormattedMessage id={'advancement.PendingItems.column.itemType'} />
        ),
        render(_, item) {
          return <PendingItemType item={item} />;
        },
      },
      {
        key: 'advancementItem',
        title: (
          <FormattedMessage id="advancement.PendingItems.column.itemDescription" />
        ),
        render(_, { type, ...item }) {
          return (
            <PendingItemAdvancementName type={type} item={item} showIcon />
          );
        },
      },
      {
        key: 'completionDate',
        dataIndex: 'date',
        title: (
          <FormattedMessage id="advancement.PendingItems.column.itemCompletionDate" />
        ),
        render(date) {
          return <DateView value={date} />;
        },
      },
    ],
    [],
  );

  if (!notApprovableItems.length || props.isLoading) return null;

  return (
    <div className={styles.notApprovableContainer}>
      <p className={styles.disclaimer}>
        <FormattedMessage id="advancement.PendingItems.nonApprovable.disclaimer" />
      </p>
      <Table
        pagination={false}
        loading={props.isLoading}
        className={styles.table}
        columns={cols}
        dataSource={notApprovableItems}
      />
    </div>
  );
}
